/**
 * Screening results awaiting a clinician's decision (ADHD-13).
 * A positive screen is not a diagnosis; the therapist records the next step.
 * Renders nothing when the client has no screening results.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import api from '../../services/api';

export const OUTCOME_LABELS = {
  refer_for_assessment: 'Refer for a full assessment',
  pre_assessment_offered: 'Offer the ADHD pre-assessment',
  no_further_assessment: 'No further assessment needed',
  already_diagnosed: 'Already diagnosed',
  client_declined: 'Client declined'
};

const SCREEN_LABELS = { adult_adhd: 'Adult ADHD screen (ASRS Part A)' };

function OutcomeRow({ outcome, onDecided }) {
  const [choice, setChoice] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/screening-outcomes/${outcome._id}`, { outcome: choice, notes });
      onDecided();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the decision. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasScore = outcome.screenScore !== null && outcome.screenScore !== undefined;
  const score = !hasScore
    ? 'score not available'
    : outcome.scoringMethod === 'item_count'
      ? `${outcome.screenScore} of ${outcome.screenMax || 6} answers in the shaded range`
      : `Part A total ${outcome.screenScore}${outcome.screenMax ? `/${outcome.screenMax}` : ''} (older scoring)`;

  return (
    <Box sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">{SCREEN_LABELS[outcome.condition] || outcome.condition}</Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date(outcome.completedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })} · {score}
        </Typography>
        <Chip size="small" label={outcome.aboveThreshold ? 'Above screening threshold' : 'Below screening threshold'}
          color={outcome.aboveThreshold ? 'warning' : 'default'} variant="outlined" />
        {outcome.status === 'decided' && <Chip size="small" label={OUTCOME_LABELS[outcome.outcome] || outcome.outcome} color="success" variant="outlined" />}
      </Box>
      {outcome.status === 'decided' && outcome.notes && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>{outcome.notes}</Typography>
      )}
      {outcome.status === 'pending_review' && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id={`next-${outcome._id}`}>Next step</InputLabel>
            <Select labelId={`next-${outcome._id}`} label="Next step" value={choice} onChange={e => setChoice(e.target.value)}>
              {Object.entries(OUTCOME_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <Button variant="contained" size="small" disabled={!choice || saving} onClick={save}>
            {saving ? 'Saving…' : 'Record decision'}
          </Button>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default function ScreeningOutcomesPanel({ clientId }) {
  const [outcomes, setOutcomes] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/screening-outcomes/client/${clientId}`);
      setOutcomes(res.data?.outcomes || []);
      setError(null);
    } catch (err) {
      // 403 means this therapist is not the client's; show nothing rather than an error.
      if (err.response?.status === 403) setOutcomes([]);
      else setError('Could not load screening results.');
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!outcomes.length) return null;

  const pending = outcomes.filter(o => o.status === 'pending_review').length;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="screening-outcomes-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Screening results</Typography>
        {pending > 0 && <Chip size="small" color="warning" label={`${pending} awaiting your decision`} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A screen is not a diagnosis. Record what happens next for this client.
      </Typography>
      {outcomes.map(o => <OutcomeRow key={o._id} outcome={o} onDecided={load} />)}
    </Paper>
  );
}
